import { types, getParent, getRoot, flow } from 'mobx-state-tree';
import * as Sentry from '@sentry/browser';
import userStatuses from '../../constants/userStatuses.json';

const URL = 'account/v1/Accounts';

const UserProfile = types
  .model('userProfile', {
    code: types.optional(types.string, ''),
    firstName: types.optional(types.string, ''),
    lastName: types.optional(types.string, ''),
    businessName: types.optional(types.string, ''),
    street: types.optional(types.string, ''),
    zipcode: types.maybe(types.string),
    city: types.optional(types.string, ''),
    state: types.optional(types.string, ''),
    country: types.optional(types.string, ''),
    createdDatetime: types.optional(types.string, ''),
    phoneNumberCountryCode: types.optional(types.string, ''),
    phoneNumber: types.optional(types.string, ''),
    phoneNumberVerified: types.optional(types.boolean, false),
    email: types.optional(types.string, ''),
    emailVerified: types.optional(types.boolean, false),
    languageId: types.optional(types.string, ''),
    defaultCurrencyId: types.optional(types.string, ''),
    defaultSourceMarketplace: types.optional(types.string, ''),
    defaultValue: types.optional(types.string, ''),
    type: types.optional(types.string, ''),
    subscriptionPlan: types.optional(types.string, ''),
    minBalance: types.maybe(types.string),
    autoReloadBalance: types.optional(types.boolean, false),
    wizardExperienceLevel: types.optional(types.string, ''),
    wizardStatus: types.optional(types.string, ''),
    listingPreferencesStatus: types.optional(types.string, ''),
    allowDuplicatesOnRelatedAccounts: types.optional(types.boolean, false),
    termsAccepted: true,
    isBetaUser: false,
    fbPixelBooted: false,
    snapchatPixelBooted: false,
    leadDynoBooted: false,
    isRtl: false,
    registeredForCourse:types.optional(types.boolean, false),
    onBoardingLevel: types.optional(
      types.union(
        types.literal('new user'),
        types.literal('registered'),
        types.literal('personal details'),
        types.literal('has store'),
        types.literal('has paypal'),
        types.literal('no plan'),
        types.literal('done onboarding'),
      ),
      'new user',
    ),
    isNonApi: false,
  })
  .views(self => ({
    get transport5007() {
      return getParent(self).transport5007;
    },
    get accountCode() {
      return getRoot(self).uiStore.sessionStore.accountCode;
    },
    get notificationsStore() {
      return getRoot(self).uiStore.notificationsStore;
    },
    get fullName() {
      return self.firstName && self.lastName ? `${self.firstName} ${self.lastName}` : '';
    },
    get basicInfoValid() {
      return (self.firstName || self.lastName) && self.phoneNumber && self.country;
    },
  }))
  .actions(self => ({
    fetchUserProfile: flow(function* fetchUserProfile() {
      try {
        self.notificationsStore.fetchNotifications();
        const { data } = yield self.transport5007.fetch(URL, {}, {});
        self.setIsBetaUser(data.isBetaUser || false);
        self.setRegisteredForCourse(data.registeredForCourse || false);
        self.setAccountCode(data.id || '');
        self.setFirstName(data.firstName || '');
        self.setLastName(data.lastName || '');
        self.setBusinessName(data.businessName || '');
        self.setStreet(data.street || '');
        self.setZipcode(data.zipCode || '');
        self.setCity(data.city || '');
        self.setState(data.state || '');
        self.setListingPreferencesStatus(data.listingPreferencesStatus || '');
        self.setCountry(data.country || '');
        self.setCreatedDatetime(data.createdDatetime || '');
        self.setPhoneNumberCountryCode(data.phoneNumberCountryCode || '');
        self.setPhoneNumber(data.phoneNumber || '');
        self.setEmail(data.email || '');
        self.setLanguageId(data.language || '');
        getRoot(self).uiStore.localeStore.changeLanguage(data.language);
        self.setDefaultCurrencyId(data.defaultCurrency || '');
        self.setDefaultSourceMarketplaceId(data.defaultSourceMarketplace || '');
        self.setCorrectDefaultValue(data.defaultSourceMarketplace ?? data.defaultCurrency)
        self.setType(data.type || '');
        self.setSubscriptionPlan(data.subscriptionPlan || '');
        self.setMinBalance(data.minBalance || '');
        self.setAutoReloadBalance(data.autoReloadBalance || false);
        self.setWizardExperienceLevel(data.wizardExperienceLevel || '');
        self.setWizardStatus(data.wizardStatus || '');
        self.setTermsAccepted(data.termsAccepted);
        self.setIsNonApi(data.isNonApi);
        getParent(self).orders.setMaximumPurchaseLimit(data.maximumPurchaseLimit || 0);
        getParent(self).orders.setNegativeProfitLimit(data.negativeProfitLimit || 0);
        getParent(self).orders.setPreferUsedLikeNew(!!data.preferUsedLikeNew);
        getParent(self).orders.setPrefferedShippingStatus(data.listingPreferencesStatus || '');
        self.setAllowDuplicatesOnRelatedAccounts(data.allowDuplicatesOnRelatedAccounts || false);
        // fetchDailyProfit
        getRoot(self).uiStore.dashboardStore.fetchDailyProfit();
        getRoot(self).uiStore.dashboardStore.fetchMiniDashboard();
        getRoot(self).uiStore.dashboardStore.fetchListingCount();
        if (data.onBoardingLevel) {
          self.onBoardingLevel = data.onBoardingLevel;
        }
        self.bootFBPixel();
        self.bootSnapchatPixel();
        self.bootLeadDyno();
        self.identifyHubspot();
        self.sendEmailToZoho();
      } catch (error) {
        console.log('getUser fail: ', error);
      }
    }),
    updateProfile: flow(function* updateProfile() {
      try {
        const payload = {
          firstName: self.firstName,
          lastName: self.lastName,
          businessName: self.businessName,
          street: self.street,
          zipCode: self.zipcode,
          city: self.city,
          state: self.state,
          country: self.country,
          phoneNumberCountryCode: self.phoneNumberCountryCode,
          phoneNumber: self.phoneNumber,
          email: self.email,
          language: self.languageId,
          defaultCurrency: self.defaultCurrencyId,
          defaultSourceMarketplace:self.defaultSourceMarketplace,
          allowDuplicatesOnRelatedAccounts: self.allowDuplicatesOnRelatedAccounts,
          type: self.type,
          isNonApi: self.isNonApi,
        };
        yield self.transport5007.update(URL, payload, {}, {});
        yield getParent(self).markets.fetchDestinations();
        getParent(self).markets.fetchSources();
        getParent(self).markets.fetchDestinationTypes();
        getRoot(self).uiStore.ordersStore.fetchInitialData();
        getRoot(self).uiStore.ordersStore.accountsBar.init();
        getRoot(self).uiStore.listingsPageStore.accountsBar.init();
        getRoot(self).uiStore.dashboardStore.fetchDailyProfit();
        getRoot(self).uiStore.dashboardStore.fetchMiniDashboard();
        getRoot(self).uiStore.dashboardStore.fetchListingCount();
        getRoot(self).uiStore.discoveryStore.getQList();
      } catch (e) {
        console.log(e);
      }
    }),
    updateCurrency: flow(function* updateCurrency(value) {
      try {
        self.setDefaultValue(value)
        const regionObject=JSON.parse(value);
        const payload = {
          firstName: self.firstName,
          lastName: self.lastName,
          businessName: self.businessName,
          street: self.street,
          zipCode: self.zipcode,
          city: self.city,
          state: self.state,
          country: self.country,
          phoneNumberCountryCode: self.phoneNumberCountryCode,
          phoneNumber: self.phoneNumber,
          email: self.email,
          language: self.languageId,
          defaultCurrency: regionObject.currency,
          defaultSourceMarketplace:regionObject.sourceMarketplace,
          allowDuplicatesOnRelatedAccounts: self.allowDuplicatesOnRelatedAccounts,
          type: self.type,
          isNonApi: self.isNonApi,
        };
        yield self.transport5007.update(URL, payload, {}, {});
        self.setDefaultCurrencyId(regionObject.currency);
        self.setDefaultSourceMarketplaceId(regionObject.sourceMarketplace)
        yield getParent(self).markets.fetchDestinations();
        getParent(self).markets.fetchSources();
        getRoot(self).uiStore.ordersStore.fetchInitialData();
        getRoot(self).uiStore.ordersStore.accountsBar.init();
        getRoot(self).uiStore.listingsPageStore.accountsBar.init();
        getRoot(self).uiStore.dashboardStore.fetchMiniDashboard();
        getRoot(self).uiStore.dashboardStore.fetchListingCount();
        getRoot(self).uiStore.dashboardStore.fetchDailyProfit();
        getRoot(self).uiStore.discoveryStore.getQList();
      } catch (e) {
        console.log(e);
      }
    }),
    updateTermsAccepted: flow(function* updateTermsAccepted(value) {
      try {
        yield self.transport5007.post(
          `${URL}/terms-accepted`,
          value,
          {},
          { 'Content-Type': 'application/json' },
        );
        self.setTermsAccepted(value);
      } catch (e) {
        console.log(e);
      }
    }),
    updateOnboardingLevel: flow(function* updateOnboardingLevel(value) {
      try {
        const newStatusIndex = userStatuses.indexOf(value);
        const oldStatusIndex = userStatuses.indexOf(self.onBoardingLevel);
        if (newStatusIndex === -1) {
          return;
        }
        if (self.onBoardingLevel) {
          if (oldStatusIndex !== -1 && oldStatusIndex > newStatusIndex) {
            return;
          }
        }
        yield self.transport5007.post(
          `${URL}/on-boarding-level?onBoardingLevel=${value}`,
          {},
          {},
          { 'Content-Type': 'application/json' },
        );
        self.setOnboardingLevel(value);
      } catch (e) {
        console.log(e);
      }
    }),
    setIsBetaUser: val => {
      self.isBetaUser = val;
    },
    setRegisteredForCourse: val => {
      self.registeredForCourse = val;
    },
    setOnboardingLevel: val => {
      self.onBoardingLevel = val;
    },
    // isNonApi
    setIsNonApi: val => {
      self.isNonApi = val;
    },
    setAccountCode: code => {
      self.code = code;
    },
    setPassword: pass => {
      self.password = pass;
    },
    setBusinessName: name => {
      self.businessName = name;
    },
    setFirstName: name => {
      self.firstName = name;
    },
    setLastName: name => {
      self.lastName = name;
    },
    setStreet: street => {
      self.street = street;
    },
    setZipcode: zipcode => {
      self.zipcode = zipcode === '' ? null : zipcode;
    },
    setListingPreferencesStatus: status => {
      self.listingPreferencesStatus = status;
    },
    setCity: city => {
      self.city = city;
    },
    setState: state => {
      self.state = state;
    },
    setCountry: country => {
      self.country = country;
    },
    setCreatedDatetime: createdDatetime => {
      self.createdDatetime = createdDatetime;
    },
    setPhoneNumber: num => {
      self.phoneNumber = num;
    },
    setPhoneNumberCountryCode: code => {
      self.phoneNumberCountryCode = code;
    },
    setEmail: email => {
      self.email = email;
      Sentry.setUser({ email });
    },
    setLanguageId: langId => {
      self.languageId = langId;

      if(langId === 'He' || langId === 'he'){
        self.isRtl=true;
      }else{
        self.isRtl=false;
      }
    },
    setDefaultCurrencyId: curr => {
      self.defaultCurrencyId = curr;
    },
    setDefaultValue: val => {
      self.defaultValue = val;
    },
    setDefaultSourceMarketplaceId: value => {
      self.defaultSourceMarketplace = value;
    },
    setCorrectDefaultValue : val => {
      switch (val) {
        case 'AZ_DE':
          self.defaultValue = JSON.stringify({currency : 'EUR',sourceMarketplace : 'AZ_DE'})
          break;
        case 'AZ_IT':
          self.defaultValue = JSON.stringify({currency : 'EUR',sourceMarketplace : 'AZ_IT'})
          break;
        case 'AZ_FR':
          self.defaultValue = JSON.stringify({currency : 'EUR',sourceMarketplace : 'AZ_FR'})
          break;
        case 'AZ_ES':
          self.defaultValue = JSON.stringify({currency : 'EUR',sourceMarketplace : 'AZ_ES'})
          break;
        case 'USD':
          self.defaultValue = JSON.stringify({currency : 'USD'})
          break;
        case 'CAD':
          self.defaultValue = JSON.stringify({currency : 'CAD'})
          break;
        case 'GBP':
          self.defaultValue =  JSON.stringify({currency : 'GBP'})
          break;
        default: break;
      }
    },
    setEmailVerified: value => {
      self.emailVerified = value;
    },
    phoneNumberVarified: value => {
      self.phoneNumberVerified = value;
    },
    setType: value => {
      self.type = value;
    },
    setSubscriptionPlan: value => {
      self.subscriptionPlan = value;
    },
    setMinBalance: value => {
      self.minBalance = value;
    },
    setAutoReloadBalance: value => {
      self.autoReloadBalance = value;
    },
    setWizardExperienceLevel: value => {
      self.wizardExperienceLevel = value;
    },
    setWizardStatus: value => {
      self.wizardStatus = value;
    },
    setAllowDuplicatesOnRelatedAccounts: e => {
      self.allowDuplicatesOnRelatedAccounts = e.target ? e.target.value : e;
    },
    setTermsAccepted: val => {
      self.termsAccepted = val;
    },
    bootFBPixel: () => {
      if (process.env.REACT_APP_ENV === 'prod' && !self.fbPixelBooted) {
        window.fbq('init', '497553034620167', {
          em: self.email,
          fn: self.firstName,
          ln: self.lastName,
          ph: self.phoneNumber,
          external_id: self.accountCode,
          ct: self.city,
          st: self.state,
          zp: self.zipcode,
          country: self.country,
        });
        self.fbPixelBooted = true;
      }
    },
    bootSnapchatPixel: () => {
      if (process.env.REACT_APP_ENV === 'prod' && !self.snapchatPixelBooted) {
        window.snaptr('init', '22734fc9-cefa-4b0e-9633-b2d003a627fb', {
          user_email: self.email,
        });
        self.snapchatPixelBooted = true;
      }
    },
    bootLeadDyno: () => {
      if (process.env.REACT_APP_ENV === 'prod' && !self.leadDynoBooted) {
        window.LeadDyno.key = '7569d290555360b8e3b2712ee2ef7df83032872e';
        window.LeadDyno.recordVisit();
        self.leadDynoBooted = true;
      }
    },
    sendEmailToZoho: () => {
      window.$zoho.salesiq.visitor.email(self.email);
    },
    appendHotjarScript: () => {
      // Add custom user attributes to identify users in Hotjar
      const hjscript = document.createElement('SCRIPT');
      hjscript.id = 'hotjar-addition';
      const userId = self.code;
      hjscript.textContent = `window.hj('identify', '${userId}', {'Signed up': '${
        self.createdDatetime
      }','User Email': '${self.email}'});`;
      document.body.appendChild(hjscript);
    },
    identifyHubspot: (data = {}) => {
      if (window._hsq) {
        window._hsq.push([
          'identify',
          {
            email: self.email,
            ...data,
          },
        ]);
      }
    },
    trackEventHubspot: (id, value) => {
      if (window._hsq && id) {
        window._hsq.push([
          'trackEvent',
          {
            id,
            value,
          },
        ]);
      }
    },
    ldRecordPurchase: purchase_amount => {
      window.LeadDyno.recordPurchase(self.email, { purchase_amount });
    },
  }));

export default UserProfile;
